import type {
  Difficulty, Domain, Misconception, QuestionFamily, QuestionKind, Skill, SkillContent, StageId,
  Subject, Subtopic, Topic, VisualKind,
} from '@/core/types';
import { bi, getLang, type Bi } from '@/i18n';

/**
 * Compact authoring format for the Studio knowledge layer.
 * Every text field accepts plain English or a bilingual `B(en, nb)` pair.
 * Older English-only packs get Norwegian through an `NbOverlay` instead.
 */
export interface ContentDef {
  intro: Bi;
  explain: Bi[];
  defs?: [Bi, Bi][];
  intuition?: [Bi, VisualKind];
  mis?: [id: string, wrong: Bi, why: Bi, fix: Bi][];
  apply?: Bi;
  keys?: [idea: Bi, keywords: string[]][];
}

export interface SkillDef {
  id: string;
  name: Bi;
  desc: Bi;
  stage: StageId;
  track?: string;
  pre?: string[];
  related?: string[];
  target?: Difficulty;
  range?: [Difficulty, Difficulty];
  aliases?: string[];
  sub?: Bi;
  c: ContentDef;
}

export interface TopicDef {
  name: Bi;
  skills: SkillDef[];
}

export interface DomainDef {
  id: string;
  name: Bi;
  desc: Bi;
  calibrate?: boolean;
  topics: TopicDef[];
}

/** Norwegian translation layer for a pack written in English. Keys are ids (or English names for topics). */
export interface NbOverlay {
  subject?: { description?: string };
  domains?: Record<string, [name: string, desc?: string]>;
  topics?: Record<string, string>;
  subs?: Record<string, string>;
  skills?: Record<string, SkillNb>;
}

export interface SkillNb {
  name: string;
  desc: string;
  intro?: string;
  explain?: string[];
  defs?: [string, string][];
  intuition?: string;
  mis?: Record<string, [wrong: string, why: string, fix: string]>;
  apply?: string;
  keys?: string[];
  /** Extra Norwegian keywords for Feynman key-idea coverage, per key idea index. */
  keywords?: string[][];
}

export interface SubjectPack {
  subject: Omit<Subject, 'domainIds'>;
  domains: DomainDef[];
  families: QuestionFamily[];
  nb?: NbOverlay;
}

export interface BuiltPack {
  subject: Subject;
  domains: Domain[];
  topics: Topic[];
  subtopics: Subtopic[];
  skills: Skill[];
  families: QuestionFamily[];
}

const en = (v: Bi | undefined): string => (v === undefined ? '' : typeof v === 'string' ? v : v.en);
const hasNb = (v: Bi | undefined): v is { en: string; nb: string } => !!v && typeof v === 'object';

const slug = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9æøå]+/g, '-').replace(/^-|-$/g, '');

/** Pick: inline Norwegian → overlay Norwegian → English. */
function pick(v: Bi | undefined, overlay: string | undefined): string {
  if (getLang() !== 'nb') return en(v);
  if (hasNb(v)) return v.nb;
  return overlay ?? en(v);
}

function buildContent(c: ContentDef, o: SkillNb | undefined): SkillContent {
  const nbArr = (arr: Bi[], ov?: string[]) => arr.map((x, i) => pick(x, ov?.[i]));
  return {
    intro: pick(c.intro, o?.intro),
    explanation: nbArr(c.explain, o?.explain),
    definitions: (c.defs ?? []).map(([term, meaning], i) => ({ term: pick(term, o?.defs?.[i]?.[0]), meaning: pick(meaning, o?.defs?.[i]?.[1]) })),
    intuition: c.intuition ? { text: pick(c.intuition[0], o?.intuition), visual: c.intuition[1] } : { text: '', visual: 'none' },
    misconceptions: (c.mis ?? []).map(([id, wrong, why, fix]): Misconception => {
      const m = o?.mis?.[id];
      return { id, wrong: pick(wrong, m?.[0]), why: pick(why, m?.[1]), fix: pick(fix, m?.[2]) };
    }),
    application: pick(c.apply, o?.apply),
    keyIdeas: (c.keys ?? []).map(([idea, keywords], i) => ({
      idea: pick(idea, o?.keys?.[i]),
      // keywords from both languages always count — a student may mix terms
      keywords: [...keywords, ...(o?.keywords?.[i] ?? [])],
    })),
  };
}

function getter<T extends object>(obj: T, key: string, fn: () => string) {
  Object.defineProperty(obj, key, { get: fn, enumerable: true, configurable: true });
}

export function buildPack(pack: SubjectPack): BuiltPack {
  const sid = pack.subject.id;
  const ov = pack.nb ?? {};
  const domains: Domain[] = [];
  const topics: Topic[] = [];
  const subtopics: Subtopic[] = [];
  const skills: Skill[] = [];

  const subject = { ...pack.subject, domainIds: [] as string[] } as Subject;
  const subjectEn = pack.subject.name;
  const subjectDescEn = pack.subject.description;
  getter(subject, 'name', () => (getLang() === 'nb' ? pack.subject.nameNo : subjectEn));
  getter(subject, 'description', () => (getLang() === 'nb' ? ov.subject?.description ?? subjectDescEn : subjectDescEn));

  for (const d of pack.domains) {
    const domain = { id: d.id, subjectId: sid, topicIds: [], calibrate: d.calibrate ?? true } as unknown as Domain;
    getter(domain, 'name', () => pick(d.name, ov.domains?.[d.id]?.[0]));
    getter(domain, 'description', () => pick(d.desc, ov.domains?.[d.id]?.[1]));
    domains.push(domain);
    for (const t of d.topics) {
      const tEn = en(t.name);
      const topicId = `${d.id}.${slug(tEn)}`;
      const topic = { id: topicId, subjectId: sid, domainId: d.id, subtopicIds: [] } as unknown as Topic;
      getter(topic, 'name', () => pick(t.name, ov.topics?.[tEn]));
      topics.push(topic);
      domain.topicIds.push(topicId);
      const subMap = new Map<string, Subtopic>();
      for (const s of t.skills) {
        const subBi = s.sub ?? t.name;
        const subEn = en(subBi);
        const subId = `${topicId}.${slug(subEn)}`;
        let sub = subMap.get(subId);
        if (!sub) {
          sub = { id: subId, subjectId: sid, domainId: d.id, topicId, skillIds: [] } as unknown as Subtopic;
          getter(sub, 'name', () => pick(subBi, ov.subs?.[subEn] ?? ov.topics?.[subEn]));
          subMap.set(subId, sub);
          subtopics.push(sub);
          topic.subtopicIds.push(subId);
        }
        sub.skillIds.push(s.id);
        const o = ov.skills?.[s.id];
        const cache = new Map<string, SkillContent>();
        const skill = {
          id: s.id,
          subjectId: sid,
          domainId: d.id,
          topicId,
          subtopicId: subId,
          stage: s.stage,
          track: s.track,
          provenance: 'STUDIO_DERIVED',
          prerequisites: s.pre ?? [],
          related: s.related ?? [],
          masteryTarget: s.target ?? 5,
          difficultyRange: s.range ?? [1, 7],
          aliases: [...(s.aliases ?? []), ...(o ? [o.name] : []), ...(hasNb(s.name) ? [s.name.nb, s.name.en] : [])],
          competenceGoalRefs: [],
          weight: 1,
        } as unknown as Skill;
        getter(skill, 'name', () => pick(s.name, o?.name));
        getter(skill, 'description', () => pick(s.desc, o?.desc));
        Object.defineProperty(skill, 'content', {
          enumerable: true,
          get: () => {
            const lang = getLang();
            let c = cache.get(lang);
            if (!c) { c = buildContent(s.c, o); cache.set(lang, c); }
            return c;
          },
        });
        skills.push(skill);
      }
    }
  }
  subject.domainIds = domains.map((d) => d.id);
  return { subject, domains, topics, subtopics, skills, families: pack.families };
}

/** Helper to declare a question family. */
export function fam(
  skillId: string,
  objective: string,
  difficulties: [Difficulty, Difficulty],
  kinds: QuestionKind[],
  generate: QuestionFamily['generate'],
  id?: string,
): QuestionFamily {
  return { id: id ?? `${skillId}#${slug(objective)}`, skillId, objective, difficulties, kinds, generate };
}

export { bi };
